import type { PlatformBrandingSettings } from '@livechat/shared';
import { brandingMessageToHtml } from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import { getPlatformBrandingSettings, updatePlatformBrandingSettings } from '../api.js';

export function PlatformSettings() {
	const [branding, setBranding] = useState<PlatformBrandingSettings>();
	const [brandingMessage, setBrandingMessage] = useState('Private and secure');
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		void getPlatformBrandingSettings()
			.then((loadedBranding) => {
				if (!current) return;
				setBranding(loadedBranding);
				setBrandingMessage(loadedBranding.message);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error ? caught.message : 'Could not load owner settings',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, []);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updatedBranding = await updatePlatformBrandingSettings({
				message: brandingMessage.trim(),
			});
			setBranding(updatedBranding);
			setBrandingMessage(updatedBranding.message);
			setMessage('Settings saved.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save owner settings');
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="workspace-section owner-settings-page">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform administration</p>
					<h2>Settings</h2>
					<p className="muted">
						Manage platform branding. Company API keys are in Customer management.
					</p>
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}

			{loading ? (
				<div className="empty-configuration">Loading owner settings…</div>
			) : (
				<form className="owner-settings-form" onSubmit={(event) => void save(event)}>
					<section className="settings-card integration-panel owner-ai-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Branding</h4>
							<p>Set the footer shown across every customer Chatbox.</p>
						</div>
						<div className="owner-ai-fields">
							<label className="owner-settings-field">
								<span>Branding message</span>
								<textarea
									maxLength={500}
									onChange={(event) => {
										setBrandingMessage(event.currentTarget.value);
										setMessage(undefined);
									}}
									required
									rows={3}
									value={brandingMessage}
								/>
								<small>
									Add secure links using{' '}
									<code>[link text](https://example.com)</code>.
								</small>
							</label>
							<div
								className="platform-branding-preview"
								dangerouslySetInnerHTML={{
									__html: brandingMessageToHtml(brandingMessage),
								}}
							/>
						</div>
						{branding?.updatedAt && (
							<small className="field-help">Branding is saved platform-wide.</small>
						)}
					</section>

					<button className="primary-button owner-settings-save" disabled={busy}>
						{busy ? 'Saving…' : 'Save settings'}
					</button>
				</form>
			)}
		</section>
	);
}
